import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import LandingShell from '../components/landing/LandingShell';
import ResultPosterTile, { hasConsent } from '../components/results/ResultPosterTile';
import { resultCards } from '../data/results';
import { compareBatchYearDesc, parseBatchYearStart } from '../lib/batchYear';

/*
 * Public /results — the results archive, reached from the navbar's "Results"
 * link and from the "See all results" card at the end of the homepage rail.
 *
 * THIS PAGE IS POSTERS ONLY. Video testimonials used to sit above the
 * scorecards here; they now have their own page at /stories, because a parent
 * comparing institutes wants results and voices as separate things, not one
 * scrolling column. A link at the foot of this page crosses to them.
 *
 * Content comes exclusively from src/data/results.js; nothing is hardcoded.
 * The consent filter runs here AND inside ResultPosterTile (double gate) — an
 * entry without consentOnFile === true cannot render from either direction.
 * An entry with no posterSrc is skipped rather than rendering a broken image.
 *
 * Newest batch first, filterable by batch year. Batch options are derived from
 * the consented data itself (never hardcoded), and the active filter lives in
 * the URL's `batch` query param so a filtered view is linkable. An empty filter
 * result renders a written message, not a blank grid.
 */

const BATCH_PARAM = 'batch';
const ALL_BATCHES = 'all';

const ResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBatch = searchParams.get(BATCH_PARAM) || ALL_BATCHES;

  const sorted = useMemo(
    () =>
      resultCards
        .filter((e) => hasConsent(e) && Boolean(e.posterSrc))
        .sort((a, b) => compareBatchYearDesc(a.batchYear, b.batchYear)),
    []
  );

  const batchOptions = useMemo(() => {
    const seen = new Set();
    const unique = [];
    sorted.forEach((entry) => {
      if (
        entry.batchYear &&
        parseBatchYearStart(entry.batchYear) !== null &&
        !seen.has(entry.batchYear)
      ) {
        seen.add(entry.batchYear);
        unique.push(entry.batchYear);
      }
    });
    return unique; // already newest-first, inherited from sorted
  }, [sorted]);

  const shown =
    activeBatch === ALL_BATCHES ? sorted : sorted.filter((e) => e.batchYear === activeBatch);

  const setBatch = (batch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (batch === ALL_BATCHES) next.delete(BATCH_PARAM);
      else next.set(BATCH_PARAM, batch);
      return next;
    });
  };

  const chip = (active) =>
    `px-3.5 py-1.5 rounded-full border text-xs lg:text-[13px] font-medium transition-colors ${
      active
        ? 'bg-brand-deep border-brand-deep text-white'
        : 'bg-white border-brand-line text-brand-ink hover:border-brand-deep'
    }`;

  return (
    <LandingShell>
      <section className="bg-brand-mist">
        <div className="mx-auto max-w-[1180px] px-5 py-9 lg:px-11 lg:py-[76px] lg:text-center">
          <h1 className="text-[25px] lg:text-[38px] font-extrabold leading-tight text-brand-deep tracking-[-0.02em] lg:tracking-[-0.025em] mb-1.5 lg:mb-2.5">
            Results
          </h1>
          <p className="text-sm lg:text-[15.5px] text-brand-slate">
            Board marks, entrance scores and university admissions — published only with written
            consent on file.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1180px] px-5 py-9 lg:px-11 lg:py-[76px]">
          {batchOptions.length > 1 && (
            <div
              className="flex flex-wrap gap-2 mb-6 lg:mb-9"
              role="group"
              aria-label="Filter by batch year"
            >
              <button
                type="button"
                onClick={() => setBatch(ALL_BATCHES)}
                aria-pressed={activeBatch === ALL_BATCHES}
                className={chip(activeBatch === ALL_BATCHES)}
              >
                All
              </button>
              {batchOptions.map((batch) => (
                <button
                  key={batch}
                  type="button"
                  onClick={() => setBatch(batch)}
                  aria-pressed={activeBatch === batch}
                  className={chip(activeBatch === batch)}
                >
                  {batch}
                </button>
              ))}
            </div>
          )}

          {sorted.length === 0 ? (
            <p className="text-sm lg:text-[15.5px] text-brand-muted border border-brand-line rounded-xl px-4 py-6 text-center">
              No consented results published yet — check back soon.
            </p>
          ) : shown.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {shown.map((entry, i) => (
                <ResultPosterTile key={entry.id} entry={entry} eager={i < 3} />
              ))}
            </div>
          ) : (
            <p className="text-sm lg:text-[15.5px] text-brand-muted border border-brand-line rounded-xl px-4 py-6 text-center">
              No published results for this batch yet — try "All" or check back soon.
            </p>
          )}
        </div>
      </section>

      {/* Crossing point to the other half of the proof — the students themselves. */}
      <section className="bg-brand-deep">
        <div className="mx-auto max-w-[1180px] px-5 py-9 lg:px-11 lg:py-14 lg:flex lg:items-center lg:justify-between lg:gap-10">
          <div>
            <h2 className="text-[22px] lg:text-[30px] font-extrabold leading-tight text-white tracking-[-0.02em] mb-1.5">
              Hear it from the students
            </h2>
            <p className="text-sm lg:text-[15px] text-white/65 mb-5 lg:mb-0">
              Unscripted, filmed at our Sector 49 centre.
            </p>
          </div>
          <Link
            to="/stories"
            className="inline-flex items-center gap-2 px-5 py-[13px] rounded-[9px] bg-brand-amber text-[14.5px] font-bold text-brand-deep"
          >
            <Play className="w-[17px] h-[17px]" fill="currentColor" />
            Watch their stories
          </Link>
        </div>
      </section>
    </LandingShell>
  );
};

export default ResultsPage;
